import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, ArrowRight, Check, Phone, Paintbrush, ShieldCheck, Clock } from "lucide-react";
import { Header } from "@/components/site/Header";
import { CTASection } from "@/components/site/CTASection";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { MobileCallBar } from "@/components/site/MobileCallBar";
import { Reviews } from "@/components/site/Reviews";
import { useLang, wizardPath } from "@/i18n/LangContext";
import { SITE, IMAGES } from "@/constants/vhm";
import { regionBySlug, regionNeighbors, areaPath, AREA_CONTENT, LANGS } from "@/constants/regions";

export default function ServiceArea() {
  const { gemeente } = useParams();
  const { lang } = useLang();
  const region = regionBySlug(gemeente);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [gemeente]);

  if (!region) return <Navigate to="/" replace />;

  const c = AREA_CONTENT[lang] || AREA_CONTENT.nl;
  const { name, zip } = region;
  const intro = lang === "nl" ? region.intro : c.introFallback(name);
  const neighbors = regionNeighbors(gemeente);
  const faq = c.faq(name);
  const badgeIcons = [ShieldCheck, Paintbrush, Clock];
  const url = `${SITE.domain}${areaPath(lang, gemeente)}`;
  const title = c.metaTitle(name);
  const description = c.metaDesc(name, zip);

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "HousePainter",
    "@id": `${SITE.domain}/#business`,
    name: `VHM Renovation — ${c.breadcrumbCrumb(name)}`,
    url,
    description,
    email: SITE.email,
    telephone: "+32499915786",
    vatID: "BE0791888501",
    priceRange: "€€",
    image: IMAGES.living,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Zoniënwoudlaan 333/101",
      postalCode: "1640",
      addressLocality: "Sint-Genesius-Rode",
      addressCountry: "BE",
    },
    areaServed: { "@type": "City", name },
    makesOffer: c.services.map((s) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: s.title },
    })),
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: c.breadcrumbHome, item: SITE.domain + "/" },
      { "@type": "ListItem", position: 2, name: c.breadcrumbCrumb(name), item: url },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={url} />
        {LANGS.map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={`${SITE.domain}${areaPath(l, gemeente)}`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${SITE.domain}${areaPath("nl", gemeente)}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={lang === "nl" ? "nl_BE" : lang} />
        <meta property="og:site_name" content="VHM Renovation" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={IMAGES.living} />
        <meta name="geo.placename" content={name} />
        <meta name="geo.region" content="BE-VBR" />
        <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <Header />

      <main>
        {/* Hero */}
        <section className="bg-beto-paper pt-[104px] pb-16 lg:pt-[132px] lg:pb-24">
          <div className="max-w-[1300px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <nav className="flex items-center gap-2 font-body text-xs text-beto-muted mb-6" data-testid="breadcrumb">
                <Link to={lang === "nl" ? "/" : `/${lang}`} className="hover:text-beto-primary transition-colors">{c.breadcrumbHome}</Link>
                <span>/</span>
                <span className="text-beto-ink font-medium">{c.breadcrumbCrumb(name)}</span>
              </nav>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-px bg-beto-primary" />
                <span className="font-body text-xs font-semibold uppercase tracking-[0.25em] text-beto-primary inline-flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" /> {name} · {zip}
                </span>
              </div>
              <h1 className="font-heading font-extrabold tracking-tight text-beto-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="area-title">
                {c.heroH1Pre} <span className="text-beto-primary">{name}</span>
              </h1>
              <p className="mt-6 font-body text-base md:text-lg text-beto-muted leading-relaxed max-w-2xl">
                {intro}
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to={wizardPath(lang)}
                  data-testid="area-cta-quote"
                  className="group inline-flex items-center gap-2 rounded-full bg-beto-primary text-white px-8 py-4 font-semibold hover:bg-beto-primaryhover transition-colors"
                >
                  {c.ctaQuote}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={SITE.phoneHref}
                  data-testid="area-cta-call"
                  className="inline-flex items-center gap-2 rounded-full border border-beto-border px-8 py-4 font-semibold text-beto-ink hover:border-beto-primary transition-colors"
                >
                  <Phone className="w-4 h-4 text-beto-primary" /> {SITE.phoneDisplay}
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-6 font-body text-sm text-beto-muted">
                {c.badges.map((b, i) => {
                  const Icon = badgeIcons[i] || Check;
                  return (
                    <span key={b} className="inline-flex items-center gap-2"><Icon className="w-4 h-4 text-beto-primary" /> {b}</span>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-2xl h-[320px] lg:h-[460px] border border-beto-border shadow-[0_20px_60px_rgba(0,0,0,0.10)]">
                <img
                  src={IMAGES.living}
                  alt={c.heroImgAlt(name)}
                  loading="eager"
                  width="1200"
                  height="800"
                  className="w-full h-full object-cover"
                  data-testid="area-hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-beto-ink/50 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 rounded-full bg-white/90 backdrop-blur px-4 py-1.5 font-body text-xs font-semibold text-beto-ink inline-flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-beto-primary" /> {name}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="bg-beto-surface py-20 lg:py-28">
          <div className="max-w-[1300px] mx-auto px-5 lg:px-8">
            <h2 className="font-heading font-extrabold tracking-tight text-beto-ink text-3xl sm:text-4xl leading-tight max-w-2xl">
              {c.servicesTitle(name)}
            </h2>
            <p className="mt-4 font-body text-base md:text-lg text-beto-muted max-w-2xl">
              {c.servicesIntro}
            </p>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="area-services">
              {c.services.map((s) => (
                <div key={s.title} className="rounded-2xl border border-beto-border bg-beto-paper p-7 hover:border-beto-primary transition-colors">
                  <Check className="w-5 h-5 text-beto-primary mb-4" />
                  <h3 className="font-heading font-bold text-lg text-beto-ink">{s.title}</h3>
                  <p className="mt-2 font-body text-sm text-beto-muted leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-beto-paper py-20 lg:py-28">
          <div className="max-w-[900px] mx-auto px-5 lg:px-8">
            <h2 className="font-heading font-extrabold tracking-tight text-beto-ink text-3xl sm:text-4xl leading-tight">
              {c.faqTitle(name)}
            </h2>
            <div className="mt-10 space-y-5" data-testid="area-faq">
              {faq.map((f) => (
                <div key={f.q} className="rounded-2xl border border-beto-border bg-beto-surface p-7">
                  <h3 className="font-heading font-bold text-lg text-beto-ink">{f.q}</h3>
                  <p className="mt-3 font-body text-sm md:text-base text-beto-muted leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Neighbours — internal linking */}
        <section className="bg-beto-surface py-16 lg:py-20">
          <div className="max-w-[1300px] mx-auto px-5 lg:px-8">
            <h2 className="font-heading font-bold text-2xl text-beto-ink">{c.neighborsTitle(name)}</h2>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="area-neighbors">
              {neighbors.map((n) => (
                <Link
                  key={n.slug}
                  to={areaPath(lang, n.slug)}
                  data-testid={`neighbor-${n.slug}`}
                  className="flex items-center gap-2 rounded-xl border border-beto-border bg-beto-paper px-4 py-3 font-body text-sm font-medium text-beto-ink hover:border-beto-primary hover:text-beto-primary transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5 text-beto-primary shrink-0" />
                  {n.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Reviews />

        <CTASection />
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileCallBar />
    </>
  );
}
